import React, { useEffect, useState } from 'react';
import { useUI } from '../../DataStore/UIContext';
import ajaxConfig from '../../util/remote/ajaxConfig';
import Notification from '../Common/Notification';
import BsModal from '../Common/BsModal';
import ActivityLoader from '../Common/ActivityLoader';

const ChangePlanModal = ({
  show,
  onClose,
  plans = [],
  currentPlanId = null,
  currentPlanName = '',
  organisationId,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const { setNotify } = useUI();

  useEffect(() => {
    if (show) {
      setSelectedPlanId(currentPlanId || null);
    }
  }, [show, currentPlanId]);

  const formatCurrency = (value, currency = 'UGX') => {
    const amount = Number(value || 0);
    return `${currency} ${amount.toLocaleString('en-UG')}`;
  };

  const handleConfirm = async () => {
    if (!organisationId || !selectedPlanId || selectedPlanId === currentPlanId) {
      onClose();
      return;
    }

    setLoading(true);

    const server_response = await ajaxConfig.changeLicensePlan({
      organisation_id: organisationId,
      new_plan_id: selectedPlanId,
    });

    setLoading(false);

    if (server_response?.status === 'OK') {
      onSuccess?.(server_response);
      setNotify(
        <Notification
          type="success"
          message={
            server_response.message || 'License plan updated successfully.'
          }
        />
      );
    } else {
      setNotify(
        <Notification
          type="danger"
          message={
            server_response?.message || 'Failed to update license plan.'
          }
        />
      );
    }

    onClose();
  };

  const selectedPlan = plans.find((p) => p.id === selectedPlanId) || null;

  // Dark palette for cards
  const baseBg = '#151827';
  const hoverBg = '#1b2030';
  const activeBg = 'linear-gradient(135deg, #252b3f 0%, #1b2030 100%)';
  const borderDefault = 'rgba(148, 163, 184, 0.3)';

  return (
    <BsModal
      show={show}
      size="modal-lg"
      onClose={onClose}
      title="Change License Plan"
      footer={
        <div>
          {loading && <ActivityLoader />}
          {!loading && (
            <>

                <div className='row'>
                    <div className='col-12 mb-2'>
                    {selectedPlan &&  <div className="alert alert-info py-2 px-3 mt-3">
                        <small className="d-block tx-12" style={{ color: '#020617' }}>
                            You are about to switch to{' '}
                            <b>{selectedPlan.planName || selectedPlan.plan_name}</b> at{' '}
                            <b>
                            {formatCurrency(
                                selectedPlan.amount,
                                selectedPlan.currency || 'UGX'
                            )}
                            </b>{' '}
                            per month. Existing prepaid coverage will remain valid, and new
                            billing will follow this plan going forward.
                        </small>
                        </div>}
                    </div>
             </div>
                
              <div className='row'>
                 <div className='col-12 text-right'>
                    <button className="btn btn-outline-light" onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={handleConfirm}
                        disabled={
                        !organisationId ||
                        !selectedPlanId ||
                        selectedPlanId === currentPlanId
                        }
                    >
                        <i className="fe fe-layers mr-1" />
                        Apply Plan Change
                    </button>
                 </div>
              </div>
           </>
          )}
        </div>
      }
    >
      {plans.length === 0 && (
        <div className="text-center text-muted py-4">
          <p className="mb-0">No plans available. Please contact support.</p>
        </div>
      )}

      {plans.length > 0 && (
        <div style={{ color: '#e5e7eb' }}>
          <p className="mb-3 tx-13" style={{ color: '#9ca3af' }}>
            Choose a plan that matches your daily voucher volume. Plan changes
            will affect your next billing cycle. Your current plan is{' '}
            <b style={{ color: '#f9fafb' }}>{currentPlanName || '—'}</b>.
          </p>

          <div className="row">
            {plans.map((plan) => {
              const planId = plan.id;
              const planName = plan.planName || plan.plan_name;
              const amount = plan.amount || 0;
              const maxV = plan.maxVouchers || plan.max_vouchers || -1;
              const currency = plan.currency || 'UGX';
              const description = plan.description || '';

              const isCurrent = currentPlanId === planId;
              const isSelected = selectedPlanId === planId;

              const isFree = amount === 0;
              const isUnlimited = maxV < 0;

              return (
                <div className="col-md-6 mb-3" key={planId}>
                  <div
                    className="p-3 rounded position-relative h-100"
                    style={{
                      cursor: 'pointer',
                      background: isCurrent || isSelected ? activeBg : baseBg,
                      borderWidth: 1,
                      borderStyle: 'solid',
                      borderColor: isCurrent
                        ? '#4f46e5'
                        : isSelected
                        ? '#38bdf8'
                        : borderDefault,
                      boxShadow:
                        isCurrent || isSelected
                          ? '0 0 0 1px rgba(15,23,42,0.4), 0 12px 30px rgba(15,23,42,0.9)'
                          : '0 0 0 1px rgba(15,23,42,0.8)',
                      transition: 'all 0.15s ease',
                    }}
                    onMouseEnter={(e) => {
                      if (!isCurrent && !isSelected) {
                        e.currentTarget.style.background = hoverBg;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isCurrent && !isSelected) {
                        e.currentTarget.style.background = baseBg;
                      }
                    }}
                    onClick={() => setSelectedPlanId(planId)}
                  >
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div>
                        <div className="d-flex align-items-center">
                          <span
                            className="tx-13 font-weight-semibold mr-2"
                            style={{ color: '#f9fafb' }}
                          >
                            {planName}
                          </span>

                          {isUnlimited && !isFree && (
                            <span
                              className="badge badge-pill"
                              style={{
                                backgroundColor: '#020617',
                                color: '#f9fafb',
                                fontSize: 10,
                              }}
                            >
                              Unlimited
                            </span>
                          )}
                        </div>
                        <div className="tx-14 mt-1">
                          {isFree ? (
                            <span style={{ color: '#e5e7eb' }}>
                              {formatCurrency(0, currency)}{' '}
                              <span style={{ color: '#9ca3af', fontSize: 11 }}>
                                per month
                              </span>
                            </span>
                          ) : (
                            <>
                              <span
                                className="font-weight-semibold"
                                style={{ color: '#f9fafb' }}
                              >
                                {formatCurrency(amount, currency)}
                              </span>{' '}
                              <span style={{ color: '#9ca3af', fontSize: 11 }}>
                                per month
                              </span>
                            </>
                          )}
                        </div>
                      </div>

                      <div className="text-right">
                        {isCurrent && (
                          <span className="badge bg-success-light text-success tx-10 d-block mb-1">
                            Current
                          </span>
                        )}
                        {!isCurrent && isSelected && (
                          <span className="badge bg-info-light text-info tx-10 d-block mb-1">
                            Selected
                          </span>
                        )}
                        <small
                          className="d-block tx-11"
                          style={{ color: '#9ca3af' }}
                        >
                          Max:{' '}
                          <b style={{ color: '#e5e7eb' }}>
                            {isUnlimited ? 'Unlimited' : `${maxV} vouchers/day`}
                          </b>
                        </small>
                      </div>
                    </div>

                    {description && (
                      <p
                        className="mb-2 tx-12"
                        style={{ color: '#d1d5db' }}
                      >
                        {description}
                      </p>
                    )}

                    <div className="d-flex justify-content-between align-items-center mt-2">
                      <small style={{ color: '#9ca3af', fontSize: 11 }}>
                        {isFree
                          ? 'Best for testing or low volume with per-voucher commissions.'
                          : 'Ideal for this business size based on daily voucher traffic.'}
                      </small>
                      {!isCurrent && (
                        <span
                          style={{
                            color: '#a855f7',
                            fontSize: 11,
                            fontWeight: 600,
                          }}
                        >
                          Tap to select
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

         
        </div>
      )}
    </BsModal>
  );
};

export default ChangePlanModal;
